import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Plus, Trash, X } from 'lucide-react';
import { useSchedulerStore } from '../../../store/schedulerStore';

interface SchedulerSettingsProps {
  onClose: () => void;
}

export function SchedulerSettings({ onClose }: SchedulerSettingsProps) {
  const { settings, updateSettings, resetSettings } = useSchedulerStore();

  const handleTimeChange = (key: 'earliestTime' | 'latestTime', value: string) => {
    updateSettings({ [key]: value });
  };

  const handleGapChange = (value: string) => {
    updateSettings({ minimumGap: parseInt(value) });
  };

  const handlePreferredTimeAdd = () => {
    const time = prompt('Enter preferred time (HH:mm format)');
    if (time && /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
      updateSettings({
        preferredTimes: [...settings.preferredTimes, time].sort()
      });
    }
  };

  const handlePreferredTimeRemove = (time: string) => {
    updateSettings({
      preferredTimes: settings.preferredTimes.filter(t => t !== time)
    });
  };

  const handleBlackoutAdd = () => {
    updateSettings({
      blackoutPeriods: [
        ...settings.blackoutPeriods,
        {
          start: '12:00',
          end: '13:00',
          days: [1, 2, 3, 4, 5]
        }
      ]
    });
  };

  const handleBlackoutRemove = (index: number) => {
    updateSettings({
      blackoutPeriods: settings.blackoutPeriods.filter((_, i) => i !== index)
    });
  };

  return (
    <div className="fixed inset-0 bg-background/95 backdrop-blur-xl z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="theme-card w-full max-w-md relative flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Scheduler Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Time Range */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Available Hours</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-muted-foreground">Earliest</label>
                <input
                  type="time"
                  value={settings.earliestTime}
                  onChange={(e) => handleTimeChange('earliestTime', e.target.value)}
                  className="w-full px-3 py-1.5 bg-muted rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Latest</label>
                <input
                  type="time"
                  value={settings.latestTime}
                  onChange={(e) => handleTimeChange('latestTime', e.target.value)}
                  className="w-full px-3 py-1.5 bg-muted rounded-lg text-sm"
                />
              </div>
            </div>
          </div>

          {/* Minimum Gap */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Minimum Gap Between Events</label>
            <input
              type="number"
              min="0"
              max="120"
              value={settings.minimumGap}
              onChange={(e) => handleGapChange(e.target.value)}
              className="w-full px-3 py-1.5 bg-muted rounded-lg text-sm"
            />
            <div className="text-xs text-muted-foreground">
              Minimum time (in minutes) between scheduled events
            </div>
          </div>

          {/* Preferred Times */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Preferred Times</label>
              <button
                onClick={handlePreferredTimeAdd}
                className="p-1 rounded-lg hover:bg-muted/70 transition-colors"
              >
                <Plus className="w-4 h-4 text-primary" />
              </button>
            </div>
            <div className="space-y-2">
              {settings.preferredTimes.map((time) => (
                <div
                  key={time}
                  className="flex items-center justify-between p-2 bg-muted rounded-lg"
                >
                  <span className="text-sm">{time}</span>
                  <button
                    onClick={() => handlePreferredTimeRemove(time)}
                    className="p-1 rounded-lg hover:bg-muted/70 transition-colors"
                  >
                    <Trash className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Blackout Periods */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Blackout Periods</label>
              <button
                onClick={handleBlackoutAdd}
                className="p-1 rounded-lg hover:bg-muted/70 transition-colors"
              >
                <Plus className="w-4 h-4 text-primary" />
              </button>
            </div>
            <div className="space-y-2">
              {settings.blackoutPeriods.map((period, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-muted rounded-lg"
                >
                  <span className="text-sm">
                    {period.start} - {period.end}
                  </span>
                  <button
                    onClick={() => handleBlackoutRemove(index)}
                    className="p-1 rounded-lg hover:bg-muted/70 transition-colors"
                  >
                    <Trash className="w-4 h-4 text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border shrink-0">
          <div className="flex gap-3">
            <button
              onClick={resetSettings}
              className="flex-1 px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/70 transition-colors"
            >
              Reset to Default
            </button>
            <button
              onClick={onClose}
              className="flex-1 theme-button px-4 py-2 rounded-lg"
            >
              Save Changes
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}